// @ts-check
/* global $ module require */
'use_strict';

import { init_additional_uploads } from "../tv_artwork/upload_additional_queue";

declare var $;

const Notifications = require('../tv_utils/utils.js').Notifications;

/**
 * Class to handle allowing the Customer to Submit messages for the Customer Correspondence Log.
 */
class CustomerChatLog {

    /**
     * @var {number} chat_id is the unique identifier for each Chat Log.
     * @var {string} chat_type is the type of chat the ajax will call out to.
     * @var {[key: string]: HTMLElement} elements are the elements necessary for Customers to submit and edit messges.
     * @var {HTMLElement} $el is the parent element the Customer Chat Log can be found in.
     */
    private chat_id: number = 0;
    private chat_type: string = '';
    private elements: {[key: string]: any} = {};
    private $el: HTMLElement;

    /**
     * Constructs the bindings for the Chat Log.
     *
     * @param {string} inventtrans_id is the unique identifier for each Chat Log.
     */
    constructor(chat_id: number, chat_type: string, $el: HTMLElement) {
        this.chat_id = chat_id;
        this.chat_type = chat_type;
        this.$el = $el;

        this.bind();

        // Interval set to check for a response.
        setInterval(
            this.check_response.bind(this),
            300000 // 5 minutes
        );
    }

    /**
     * Bind all the elements for the Chat Log Modal.
     *
     * @returns {void}
     */
    bind() : void {
        this.elements.entered_message = this.$el.querySelector(`.js-chat-log-message-${this.chat_id}`);
        this.elements.submit_button = this.$el.querySelector(`.js-chat-log-button-submit-${this.chat_id}`);
        this.elements.submit_button.addEventListener('click', this.validate_message.bind(this));
        this.elements.log_scroll_box = document.getElementById(`js-chat-log-init-${this.chat_id}`);
    }

    /**
     * Method to validate the entered message and send it off
     * to be added to the Chat Correspondence Log.
     *
     * @returns {void}
     */
    validate_message() : void {
        // Trim message.
        const msg = this.elements.entered_message.value.trim();
        this.elements.entered_message.value = msg;

        // Validate message.
        if (msg.length < 1) {
            this.elements.entered_message.reportValidity();
            return;
        }

        const current_epoch = Math.floor(new Date().getTime()/1000.0);
        const data = {
            'chat_id': this.chat_id,
            'timestamp': current_epoch,
            'user': 'Customer',
            'message': msg
        };

        $.ceAjax('request', `index.php?dispatch=tv_utils.${this.chat_type}_chat`, {
            'result_ids': `js-chat-log-init-${this.chat_id}`,
            'full_render': true,
            'method': 'POST',
            'caching': false,
            'data': data,
            'callback': this.handle_response.bind(this)
        });
    }

    /**
     * Call Back Handler to pop a notification of success to the customer,
     * then clear the message box and scroll the log down to the new message.
     *
     * @returns {void}
     */
    handle_response() : void {
        // Show user that it was submitted.
        Notifications.set_notification(
            'N',
            'Message submitted successfully.',
            'tv_chat_log_message_successful',
            true
        );
        // Clear message input.
        this.elements.entered_message.value = '';
        // Scroll the Chat Log down to the new message.
        this.elements.log_scroll_box.scrollTop = this.elements.log_scroll_box.scrollHeight;
    }

    check_response() : void {
        $.ceAjax('request', `index.php?dispatch=tv_utils.${this.chat_type}_chat_init`, {
            'result_ids': `js-chat-log-init-${this.chat_id}`,
            'full_render': true,
            'method': 'POST',
            'caching': false,
            'hidden': true,
            'data': { chat_id: this.chat_id }
        });
    }
}

/**
 * Class to Initialize the Correspondence Logger.
 */
export class CustomerChat {

    /**
     * @var {MutationObserver} observer to watch when the Initialization is successful.
     * @var {string} inventtrans_id is the unique identifier for each Chat Log.
     * @var {HTMLElement} log_scroll_box is the element that contains the scroll bar for the Log.
     * @var {HTMLElement} $el is the parent element the Customer Chat can be found in.
     */
    observer: MutationObserver;
    chat_id: number = 0;
    chat_type: string = '';
    log_scroll_box: HTMLElement;
    $el: HTMLElement;

    /**
     * Init the observer and provide the callback to use when a change occurs in the Chat Log Init.
     * Then fire off the request to init the Correspondence Log.
     */
    constructor(chat_id: HTMLInputElement) {
        this.chat_id = parseInt(chat_id.value, 10);
        this.$el = chat_id.parentElement;
        this.chat_type = (<HTMLInputElement>this.$el.querySelector(`.js-chat-type-${this.chat_id}`)).value;
        this.log_scroll_box = this.$el.querySelector(`#js-chat-log-init-${this.chat_id}`);

        this.observer = new MutationObserver(this.init_chat_log.bind(this));
        this.observer.observe(this.log_scroll_box, { childList: true });

        const chat_log_openers = document.getElementsByClassName(`js-chat-log-modal-opener-${this.chat_id}`);
        for (const chat_log_opener of chat_log_openers) {
            chat_log_opener.addEventListener('click', () => {
                // Check to see if a modal is being displayed and close it.
                const modal = $.ceDialog('get_last')[0];
                if (modal && modal.style.display === 'block') {
                    $.ceDialog('get_last').ceDialog('close');
                }
                // Initialize Additional Artwork within the Chat Log.
                const upload_additional_queue_id = <HTMLInputElement>this.$el.querySelector(
                    '.js-tv-artwork-upload-additional-id'
                );
                if (upload_additional_queue_id) {
                    init_additional_uploads(upload_additional_queue_id);
                }
                this.init_request();
            });
        }
    }

    /**
     * Make the request to the controller to initiailize the Customer Chat Log.
     *
     * @returns {void}
     */
    init_request() : void {
        $.ceAjax('request', `index.php?dispatch=tv_utils.${this.chat_type}_chat_init`, {
            'result_ids': `js-chat-log-init-${this.chat_id}`,
            'full_render': true,
            'method': 'POST',
            'caching': false,
            'hidden': true,
            'data': { chat_id: this.chat_id },
            'callback': results => {
                // Fallback for Multiples on one page.
                if (!this.log_scroll_box.querySelector('#js-chat-log-block')) {
                    this.log_scroll_box.innerHTML = results.html[`js-chat-log-init-${this.chat_id}`];
                }
            }
        });
    }

    /**
     * Check to see if the Customer Chat Log element is present and if it is,
     * we want to stop the mutation observer and initialize the
     * Customer Chat Log class.
     *
     * @returns {void}
     */
    init_chat_log() : void {
        if (!this.log_scroll_box.querySelector('#js-chat-log-block')) {
            return;
        }
        // Disconnect Mutation Observer.
        this.observer.disconnect();
        // Scroll the Log to the latest message.
        this.log_scroll_box.scrollTop = this.log_scroll_box.scrollHeight;
        (() => new CustomerChatLog(this.chat_id, this.chat_type, this.$el))();
    }
}

/**
 * The handler to fetch all the Inventtrans IDs that are available
 * for each Customer Correspondence on the page.
 */
export class CustomerChatHandler {
    constructor() {
        const chat_ids = <HTMLCollectionOf<HTMLInputElement>> document.getElementsByClassName('chat_id');
        for (const chat_id of chat_ids) {
            (() => new CustomerChat(chat_id))();
        }
    }
}
